import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import {
  getStudent, getStudents, updateStudent, planHhbLetters, finalizeHhbLetters,
  listHhbLetters, nameCheckHhbLetter, measureHhbLetter,
} from "../../lib/api";
import HhbPrintPanel from "./HhbPrintPanel";

const INCIDENT_TYPES = ["Bullying", "Harassment", "Hazing", "Bullying and/or Harassment"];
const EMPTY_G = { full_name: "", greeting: "", address_1: "", address_2: "", city: "", state: "", zip: "", receives_hhb: true };

function hasAddr(g) { return !!(g.address_1 || g.city); }

// Build a guardian address from the student's mailing address.
function mailingAddr(s) {
  if (!s) return { address_1: "", address_2: "", city: "", state: "", zip: "" };
  const street = (s.mailing_street || "").trim();
  if (street || s.mailing_city || s.mailing_state || s.mailing_zip) {
    return {
      address_1: street,
      address_2: "",
      city: (s.mailing_city || "").trim(),
      state: (s.mailing_state || "").trim(),
      zip: (s.mailing_zip || "").trim(),
    };
  }
  const legacy = (s.mailing_address || "").trim();
  if (legacy) return { address_1: legacy, address_2: "", city: "", state: "", zip: "" };
  return { address_1: "", address_2: "", city: "", state: "", zip: "" };
}
function hasMailing(s) {
  const a = mailingAddr(s);
  return !!(a.address_1 || a.city);
}

// Mirror of backend resolve_pronoun_set: does this student have usable pronouns
// on file? (standard he/she/they or a custom slash/comma form of 3+ parts).
function hasPronouns(s) {
  const p = ((s && (s.pronouns || s.pronoun_set)) || "").trim().toLowerCase();
  if (!p || p === "other") return false;
  if (p.startsWith("she") || p.startsWith("they") || p.startsWith("he")) return true;
  return p.split(/[/,]/).map((x) => x.trim()).filter(Boolean).length >= 3;
}

// Mirror of backend helpers.hhb_letters.name_check so the confidentiality
// warning updates instantly (on every keystroke) and client+server always agree.
const COMMON_INITIALS_WORDS = new Set([
  "is", "it", "in", "on", "or", "an", "as", "at", "be", "we", "he", "me",
  "my", "of", "to", "do", "go", "so", "no", "up", "us", "am", "by", "if",
  "ok", "oh", "ah", "hi", "id", "ye", "ha", "hm", "re", "ex", "pm",
]);
function _escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
function _initialLetters(s) {
  const first = ((s.first_name || s.preferred_name) || "").trim();
  const last = (s.last_name || "").trim();
  const out = [];
  if (first.slice(0, 1)) out.push(first.slice(0, 1).toUpperCase());
  if (last.slice(0, 1)) out.push(last.slice(0, 1).toUpperCase());
  return out;
}
function _initialsMatch(text, letters) {
  if (letters.length < 2) return null;
  const core = letters.map(_escRe).join("[.\\s]*");
  const re = new RegExp("(?<![A-Za-z0-9])" + core + "\\.?(?![A-Za-z0-9])", "gi");
  let m;
  while ((m = re.exec(text)) !== null) {
    const matched = m[0];
    const coreText = matched.endsWith(".") ? matched.slice(0, -1) : matched;
    const between = coreText.slice(1);
    const hasInternalSep = /[.\s]/.test(between);
    const lettersOnly = coreText.replace(/[^A-Za-z0-9]/g, "");
    if (hasInternalSep) return matched;
    const allUpper = lettersOnly === lettersOnly.toUpperCase() && lettersOnly !== lettersOnly.toLowerCase();
    if (allUpper) return matched;
    if (!COMMON_INITIALS_WORDS.has(lettersOnly.toLowerCase())) return matched;
  }
  return null;
}
function localNameCheck(text, roster, subjectId) {
  if (!text) return [];
  const found = [];
  const seen = new Set();
  const subject = roster.find((s) => s.id === subjectId);
  const subjLetters = subject ? _initialLetters(subject) : [];
  for (const s of roster) {
    if (s.id === subjectId) continue;
    const first = ((s.preferred_name || s.first_name) || "").trim();
    const legalFirst = (s.first_name || "").trim();
    const last = (s.last_name || "").trim();
    const variants = new Set();
    for (const fn of [first, legalFirst]) { if (fn && last) variants.add(`${fn} ${last}`); }
    for (const tok of [first, legalFirst, last]) { if (tok && tok.length >= 3) variants.add(tok); }
    for (const vv of variants) {
      if (seen.has(vv.toLowerCase())) continue;
      const re = new RegExp("\\b" + _escRe(vv) + "\\b", "i");
      if (re.test(text)) { found.push(vv); seen.add(vv.toLowerCase()); }
    }
    const letters = _initialLetters(s);
    if (letters.length && JSON.stringify(letters) !== JSON.stringify(subjLetters)) {
      const im = _initialsMatch(text, letters);
      if (im && !seen.has(im.toLowerCase().trim())) { found.push(im); seen.add(im.toLowerCase().trim()); }
    }
  }
  return found;
}

export default function LetterWizard({ invId, inv, stage, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const outcome = stage === "outcome";
  const findingFromStatus = inv.status === "Closed - Substantiated" ? "substantiated"
    : inv.status === "Closed - Not Substantiated" ? "not_substantiated" : "substantiated";

  const [meta, setMeta] = useState({
    incident_type: inv.hhb_incident_type || "Bullying and/or Harassment",
    incident_descriptor: inv.hhb_incident_descriptor || "",
    criminal_language: inv.hhb_criminal_language !== false,
    letter_date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    finding: findingFromStatus,
    finding_summary: "", not_met_explanation: "", measures_taken: "",
  });

  // Step 2 — guardians per student
  const [students, setStudents] = useState({});   // id -> student doc
  const [guardians, setGuardians] = useState({});  // id -> [guardian]
  const [loading, setLoading] = useState(false);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    const ids = [inv.victim_id, inv.aggressor_id].filter(Boolean);
    const sMap = {}, gMap = {};
    for (const id of ids) {
      try {
        const { data } = await getStudent(id);
        sMap[id] = data;
        const addr = mailingAddr(data);
        // Default guardians (no saved letter_guardians): first + optional second
        // contact, each pre-filled with the student's mailing address.
        const defaults = [];
        if (data.first_contact_name) defaults.push({ ...EMPTY_G, id: `g_${id}_1`, full_name: data.first_contact_name, greeting: data.first_contact_name, ...addr });
        if (data.second_contact_name) defaults.push({ ...EMPTY_G, id: `g_${id}_2`, full_name: data.second_contact_name, greeting: data.second_contact_name, ...addr });
        if (!defaults.length) defaults.push({ ...EMPTY_G, id: `g_${id}_1` });
        const source = (data.letter_guardians && data.letter_guardians.length) ? data.letter_guardians : defaults;
        gMap[id] = source.map((g, i) => {
          const merged = { ...EMPTY_G, ...g, id: g.id || `g_${id}_${i + 1}` };
          // Fill a blank address from the student's mailing address; never overwrite
          // an address someone typed in by hand.
          if (!merged.address_1 && !merged.city) Object.assign(merged, addr);
          return merged;
        });
      } catch { /* ignore */ }
    }
    setStudents(sMap); setGuardians(gMap); setLoading(false);
  }, [inv.victim_id, inv.aggressor_id]);

  useEffect(() => { if (step === 2) loadStudents(); }, [step, loadStudents]);

  const setG = (sid, idx, patch) =>
    setGuardians((p) => ({ ...p, [sid]: p[sid].map((g, i) => (i === idx ? { ...g, ...patch } : g)) }));
  const addG = (sid) =>
    setGuardians((p) => ({ ...p, [sid]: [...p[sid], { ...EMPTY_G, id: `g_${sid}_${p[sid].length + 1}` }] }));
  const removeG = (sid, idx) =>
    setGuardians((p) => ({ ...p, [sid]: p[sid].filter((_, i) => i !== idx) }));

  const saveGuardians = async () => {
    try {
      for (const sid of Object.keys(guardians)) {
        await updateStudent(sid, { ...students[sid], letter_guardians: guardians[sid] });
      }
      toast.success("Recipients saved");
      return true;
    } catch (e) { toast.error("Failed to save recipients"); return false; }
  };

  // Step 3 — plan + edit
  const [letters, setLetters] = useState([]);
  const [planning, setPlanning] = useState(false);
  const [roster, setRoster] = useState([]);

  const loadRoster = useCallback(async () => {
    try {
      const { data } = await getStudents(null, true);
      setRoster((data || []).map((s) => ({
        id: s.id, first_name: s.first_name, last_name: s.last_name, preferred_name: s.preferred_name,
      })));
    } catch { /* ignore — server-side check still enforces */ }
  }, []);

  const runPlan = useCallback(async () => {
    setPlanning(true);
    try {
      const { data } = await planHhbLetters(invId, {
        stage, finding: outcome ? meta.finding : undefined,
        incident_type: meta.incident_type, incident_descriptor: meta.incident_descriptor,
        criminal_language: meta.criminal_language, letter_date: meta.letter_date,
        finding_summary: meta.finding_summary, not_met_explanation: meta.not_met_explanation,
        measures_taken: meta.measures_taken,
      });
      setLetters((data.letters || []).map((l) => ({ ...l, include: !l.skip })));
    } catch { toast.error("Failed to build letters"); }
    setPlanning(false);
  }, [invId, stage, outcome, meta]);

  useEffect(() => { if (step === 3) { runPlan(); loadRoster(); } }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const [finalized, setFinalized] = useState(null);
  const [finalizing, setFinalizing] = useState(false);

  const doFinalize = async () => {
    const chosen = letters.filter((l) => l.include && !l.skip);
    if (!chosen.length) { toast.error("Select at least one letter"); return; }
    const leaking = chosen.filter((l) => (l.name_matches || []).length);
    if (leaking.length) { toast.error("Fix confidentiality issues before finalizing"); return; }
    setFinalizing(true);
    try {
      const { data } = await finalizeHhbLetters(invId, {
        stage, finding: outcome ? meta.finding : undefined,
        incident_type: meta.incident_type, incident_descriptor: meta.incident_descriptor,
        criminal_language: meta.criminal_language, letter_date: meta.letter_date,
        finding_summary: meta.finding_summary, not_met_explanation: meta.not_met_explanation,
        measures_taken: meta.measures_taken,
        letters: chosen,
      });
      toast.success(`Finalized ${data.count} letters`);
      const list = await listHhbLetters(invId);
      setFinalized((list.data || []).filter((l) => l.stage === stage && !l.superseded));
      setStep(4);
      onDone && onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.blocked) toast.error(`Blocked: ${d.blocked.map((b) => b.matches.join(", ")).join("; ")}`);
      else toast.error(typeof d === "string" ? d : "Finalize failed");
    }
    setFinalizing(false);
  };

  const title = outcome ? "Generate Outcome Letters" : "Generate Initiation Letters";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="letter-wizard">
        <DialogHeader>
          <DialogTitle>{title} — Step {step} of 4</DialogTitle>
          <DialogDescription>{inv.victim_name} (victim) · {inv.aggressor_name} (perpetrator)</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Incident type</Label>
                <Select value={meta.incident_type} onValueChange={(v) => setMeta((p) => ({ ...p, incident_type: v }))}>
                  <SelectTrigger data-testid="wiz-incident-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{INCIDENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Optional descriptor</Label>
                <Input value={meta.incident_descriptor} placeholder="e.g. sexual harassment" onChange={(e) => setMeta((p) => ({ ...p, incident_descriptor: e.target.value }))} data-testid="wiz-descriptor" />
              </div>
              <div className="space-y-1.5">
                <Label>Letter date</Label>
                <Input value={meta.letter_date} onChange={(e) => setMeta((p) => ({ ...p, letter_date: e.target.value }))} data-testid="wiz-letter-date" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer pt-6" data-testid="wiz-criminal">
                <input type="checkbox" checked={meta.criminal_language} onChange={(e) => setMeta((p) => ({ ...p, criminal_language: e.target.checked }))} />
                Include possible criminal charge language in perpetrator letters
              </label>
            </div>
            {outcome && (
              <div className="space-y-3 border-t pt-3">
                <div className="space-y-1.5">
                  <Label>Finding</Label>
                  <Select value={meta.finding} onValueChange={(v) => setMeta((p) => ({ ...p, finding: v }))}>
                    <SelectTrigger data-testid="wiz-finding"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="substantiated">Substantiated</SelectItem>
                      <SelectItem value="not_substantiated">Not Substantiated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-amber-600">This text may appear in letters to BOTH families. Do not use any student's name; refer to "the other student" or "the perpetrator/victim."</p>
                {meta.finding === "substantiated" ? (
                  <div className="space-y-1.5">
                    <Label>What the investigation found ({`{{finding_summary}}`})</Label>
                    <textarea rows={2} className="w-full text-sm border rounded px-2 py-1.5" value={meta.finding_summary} onChange={(e) => setMeta((p) => ({ ...p, finding_summary: e.target.value }))} data-testid="wiz-finding-summary" />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>Why it does not meet the definition ({`{{not_met_explanation}}`})</Label>
                    <textarea rows={2} className="w-full text-sm border rounded px-2 py-1.5" value={meta.not_met_explanation} onChange={(e) => setMeta((p) => ({ ...p, not_met_explanation: e.target.value }))} data-testid="wiz-not-met" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Measures taken ({`{{measures_taken}}`})</Label>
                  <textarea rows={2} className="w-full text-sm border rounded px-2 py-1.5" value={meta.measures_taken} onChange={(e) => setMeta((p) => ({ ...p, measures_taken: e.target.value }))} data-testid="wiz-measures" />
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wiz-step2">
            {loading ? <div className="h-32 bg-gray-100 rounded animate-pulse" /> : Object.keys(students).map((sid) => (
              <div key={sid} className="border rounded-lg p-3">
                <p className="font-medium text-sm mb-2">{students[sid].first_name} {students[sid].last_name}
                  <span className="text-xs text-muted-foreground ml-2">District: {students[sid].school_district || "— none on file —"}</span></p>
                {!hasPronouns(students[sid]) && (
                  <p className="text-xs text-amber-700 mb-2 flex items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`wiz-no-pronouns-${sid}`}>
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    No pronouns on file for {students[sid].first_name || "this student"} — letters will use {students[sid].first_name ? `${students[sid].first_name}'s name` : "the student's name"} instead.
                    <a href={`/students/${sid}?tab=demographics`} target="_blank" rel="noreferrer" className="underline font-medium ml-1 whitespace-nowrap">Add in demographics</a>
                  </p>
                )}
                {(guardians[sid] || []).map((g, idx) => (
                  <div key={g.id} className="border rounded p-2 mb-2 space-y-2 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <Input className="h-8" placeholder="Guardian full name" value={g.full_name} onChange={(e) => setG(sid, idx, { full_name: e.target.value })} />
                      <Input className="h-8" placeholder="Greeting (Ms. Smith)" value={g.greeting} onChange={(e) => setG(sid, idx, { greeting: e.target.value })} />
                      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => removeG(sid, idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input className="h-8" placeholder="Address line 1" value={g.address_1} onChange={(e) => setG(sid, idx, { address_1: e.target.value })} />
                      <Input className="h-8" placeholder="Address line 2" value={g.address_2} onChange={(e) => setG(sid, idx, { address_2: e.target.value })} />
                      <Input className="h-8" placeholder="City" value={g.city} onChange={(e) => setG(sid, idx, { city: e.target.value })} />
                      <div className="flex gap-2">
                        <Input className="h-8" placeholder="State" value={g.state} onChange={(e) => setG(sid, idx, { state: e.target.value })} />
                        <Input className="h-8" placeholder="ZIP" value={g.zip} onChange={(e) => setG(sid, idx, { zip: e.target.value })} />
                      </div>
                    </div>
                    {hasMailing(students[sid]) && (
                      <button type="button" className="text-xs text-[#00305E] underline hover:no-underline" onClick={() => setG(sid, idx, mailingAddr(students[sid]))} data-testid={`wiz-use-mailing-${sid}-${idx}`}>
                        Use student mailing address
                      </button>
                    )}
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={g.receives_hhb !== false} onChange={(e) => setG(sid, idx, { receives_hhb: e.target.checked })} />
                      This is where official HHB letters should be mailed
                      {!hasAddr(g) && g.receives_hhb !== false && <span className="text-red-600 ml-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> add a mailing address</span>}
                    </label>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addG(sid)}><Plus className="w-3.5 h-3.5 mr-1" />Add guardian</Button>
                {!students[sid].school_district && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No Sending School/District on file — the LEA letter can't be addressed. Set it on the student profile.</p>}
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="wiz-step3">
            {planning ? <div className="h-40 bg-gray-100 rounded animate-pulse" /> : letters.map((l, idx) => (
              <div key={l.temp_id} className={`border rounded-lg p-3 ${l.skip ? "opacity-60" : ""}`} data-testid={`wiz-letter-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{l.label} → {l.recipient_name || <span className="text-red-600">no recipient</span>}</p>
                    {(l.missing || []).length > 0 && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Missing: {l.missing.join(", ")}</p>}
                  </div>
                  {!l.skip && (
                    <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                      <input type="checkbox" checked={l.include} onChange={(e) => setLetters((p) => p.map((x, i) => i === idx ? { ...x, include: e.target.checked } : x))} data-testid={`wiz-include-${idx}`} /> Include
                    </label>
                  )}
                </div>
                {!l.skip && (
                  <>
                    <textarea rows={6} className={`w-full text-xs border rounded px-2 py-1.5 mt-2 ${(l.name_matches || []).length ? "border-red-500 bg-red-50" : ""}`}
                      value={l.body_text}
                      onChange={(e) => {
                        const text = e.target.value;
                        setLetters((p) => p.map((x, i) => i !== idx ? x
                          : { ...x, body_text: text, name_matches: localNameCheck(text, roster, x.subject_student_id) }));
                      }}
                      onBlur={async () => {
                        try {
                          const { data } = await nameCheckHhbLetter(invId, { subject_student_id: l.subject_student_id, other_student_id: l.other_student_id, text: l.body_text });
                          setLetters((p) => p.map((x, i) => i === idx ? { ...x, name_matches: data.matches } : x));
                        } catch { /* ignore — live check already covers this */ }
                        try {
                          const { data } = await measureHhbLetter(invId, { body_text: l.body_text, recipient_block: l.recipient_block, greeting: l.greeting, letter_date: meta.letter_date });
                          setLetters((p) => p.map((x, i) => i === idx ? { ...x, page_count: data.pages } : x));
                        } catch { /* ignore */ }
                      }}
                      data-testid={`wiz-body-${idx}`} />
                    {(l.name_matches || []).length > 0 && (
                      <p className="text-xs text-red-600 font-medium mt-1 flex items-center gap-1" data-testid={`wiz-leak-${idx}`}>
                        <AlertTriangle className="w-3 h-3" /> Contains another student's name/initials: {l.name_matches.join(", ")} — remove before finalizing.
                      </p>
                    )}
                    {l.page_count > 1 && (
                      <p className="text-xs text-amber-700 font-medium mt-1 flex items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`wiz-overflow-${idx}`}>
                        <AlertTriangle className="w-3 h-3" /> This letter runs onto {l.page_count} pages even after auto-fit — shorten the text to keep it on one page.
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {step === 4 && finalized && (
          <div className="space-y-3" data-testid="wiz-step4">
            <HhbPrintPanel invId={invId} stage={stage} />
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && step < 4 && <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          {step === 1 && <Button className="bg-[#00305E]" onClick={() => setStep(2)} data-testid="wiz-next-1">Next: Recipients</Button>}
          {step === 2 && <Button className="bg-[#00305E]" onClick={async () => { if (await saveGuardians()) { setStep(3); } }} data-testid="wiz-next-2">Save &amp; Preview</Button>}
          {step === 2 && <Button variant="ghost" onClick={() => setStep(3)}>Skip saving</Button>}
          {step === 3 && <Button className="bg-[#00305E]" onClick={doFinalize} disabled={finalizing || letters.some((l) => l.include && !l.skip && (l.name_matches || []).length)} data-testid="wiz-finalize">{finalizing ? "Finalizing…" : "Finalize letters"}</Button>}
          {step === 4 && <Button className="bg-[#00305E]" onClick={onClose} data-testid="wiz-done">Done</Button>}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>

        {step === 3 && letters.length === 0 && !planning && (
          <p className="text-center text-sm text-muted-foreground" data-testid="wiz-plan-prompt">
            <Button variant="outline" size="sm" onClick={runPlan}>Build letter previews</Button>
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// auto-build previews when entering step 3
LetterWizard.displayName = "LetterWizard";
